import React, { useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';

export type WorkstreamStatusOption = 'Not Started' | 'In Progress' | 'Complete' | 'Blocked';

export interface WorkstreamRow {
  id?: string;
  name: string;
  owner: string;
  status: string;
  target: string;
  notes: string;
}

const WORKSTREAM_STATUSES: WorkstreamStatusOption[] = [
  'Not Started',
  'In Progress',
  'Complete',
  'Blocked',
];

const STATUS_TEXT: Record<string, string> = {
  'In Progress': 'text-blue-700',
  Complete: 'text-emerald-700',
  'Not Started': 'text-slate-500',
  Blocked: 'text-amber-700',
};

const STATUS_DOT: Record<string, string> = {
  'In Progress': 'bg-blue-500',
  Complete: 'bg-emerald-500',
  'Not Started': 'bg-slate-300',
  Blocked: 'bg-amber-500',
};

const FIELD_INPUT =
  'w-full rounded-lg border border-[#e5e7eb] bg-white px-3 py-2 text-xs leading-5 text-[#18365E] outline-none transition-colors placeholder:text-[#94a3b8] focus:border-[#18365E]/40 focus:ring-2 focus:ring-[#18365E]/10';

const FIELD_LABEL =
  'block text-[10px] font-semibold uppercase tracking-wide leading-4 text-[#717182]';

/** DMD-1208: mark mandatory fields with a visible asterisk. */
function FieldLabel({
  children,
  required = false,
}: {
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <span className={FIELD_LABEL}>
      {children}
      {required && (
        <span className="text-[#FF6E4C] ml-0.5" aria-hidden="true">
          *
        </span>
      )}
      {required && <span className="sr-only"> (required)</span>}
    </span>
  );
}

function normalizeStatus(raw: string): WorkstreamStatusOption {
  const match = WORKSTREAM_STATUSES.find((s) => s.toLowerCase() === raw.toLowerCase());
  return match ?? 'Not Started';
}

/** Convert stored target (date string / Ongoing / free text) to YYYY-MM-DD for <input type="date">. */
function toDateInputValue(raw: string): string {
  const text = (raw || '').trim();
  if (!text) return '';
  if (['ongoing', 'tbd', 'n/a', '-', '—'].includes(text.toLowerCase())) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Listing display: empty / Ongoing-like values show as Ongoing. */
function formatTargetForDisplay(raw: string): string {
  const text = (raw || '').trim();
  if (!text || ['ongoing', 'tbd', 'n/a', '-', '—'].includes(text.toLowerCase())) {
    return 'Ongoing';
  }
  return text;
}

/** Display placeholders like "Unassigned" must not round-trip into the Owner field. */
function normalizeOwnerForForm(raw: string): string {
  const text = raw.trim();
  if (!text || text.toLowerCase() === 'unassigned') return '';
  return text;
}

/** No id: the save handler treats a missing id as "create". */
function emptyForm(): WorkstreamRow {
  return {
    name: '',
    owner: '',
    status: 'Not Started',
    target: '',
    notes: '',
  };
}

function StatusSelect({
  status,
  onChange,
  required = false,
}: {
  status: WorkstreamStatusOption;
  onChange: (next: WorkstreamStatusOption) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required || undefined}
        onClick={() => setOpen((v) => !v)}
        className={`${FIELD_INPUT} flex items-center gap-2 text-left hover:border-[#cbd5e1]`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[status] ?? 'bg-slate-300'}`} />
        <span className={`flex-1 font-semibold ${STATUS_TEXT[status] ?? 'text-slate-500'}`}>
          {status}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-[#94a3b8] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-[#e5e7eb] bg-white shadow-lg"
        >
          {WORKSTREAM_STATUSES.map((option) => {
            const selected = option === status;
            return (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-[#F5F7FA] ${selected ? 'bg-[#F5F7FA]' : ''
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[option]}`} />
                <span className={`flex-1 text-xs font-semibold ${STATUS_TEXT[option]}`}>{option}</span>
                {selected && <Check className="w-3.5 h-3.5 text-[#18365E]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WorkstreamFormModal({
  mode,
  initial,
  onClose,
  onSave,
}: {
  mode: 'add' | 'edit';
  initial: WorkstreamRow;
  onClose: () => void;
  onSave: (row: WorkstreamRow) => Promise<void>;
}) {
  const [form, setForm] = useState<WorkstreamRow>({
    ...initial,
    status: normalizeStatus(initial.status),
    // Date picker shows empty when target is Ongoing / blank
    target: toDateInputValue(initial.target),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const canSave = form.name.trim().length > 0 && !saving;
  const selectedDate = toDateInputValue(form.target);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const setField = <K extends keyof WorkstreamRow>(key: K, value: WorkstreamRow[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    // Empty date → Ongoing (allowed)
    const target = selectedDate || 'Ongoing';

    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        owner: normalizeOwnerForForm(form.owner),
        target,
        notes: form.notes.trim(),
        status: normalizeStatus(form.status),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save workstream.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-xl shadow-xl max-w-lg w-full flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e5e7eb]">
          <h3 id={titleId} className="text-sm font-semibold text-[#18365E]">
            {mode === 'add' ? 'Add workstream' : 'Edit workstream'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1.5 hover:bg-[#F5F7FA] rounded-lg disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#717182]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <label className="block space-y-1.5">
            <FieldLabel required>Workstream</FieldLabel>
            <input
              autoFocus
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g. Data and Analytics"
              required
              aria-required="true"
              className={FIELD_INPUT}
            />
          </label>

          <label className="block space-y-1.5">
            <FieldLabel>Owner</FieldLabel>
            <input
              type="text"
              value={form.owner}
              onChange={(e) => setField('owner', e.target.value)}
              placeholder="Optional — leave blank if unassigned"
              className={FIELD_INPUT}
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <FieldLabel required>Status</FieldLabel>
              <StatusSelect
                status={normalizeStatus(form.status)}
                onChange={(next) => setField('status', next)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <FieldLabel>Target</FieldLabel>
              <div className="relative">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setField('target', e.target.value);
                    if (error) setError(null);
                  }}
                  aria-describedby={`${titleId}-target-hint`}
                  className={`${FIELD_INPUT} ${selectedDate ? 'pr-8' : ''}`}
                />
                {selectedDate && (
                  <button
                    type="button"
                    onClick={() => {
                      setField('target', '');
                      if (error) setError(null);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-5 w-5 items-center justify-center rounded-full text-[#94a3b8] hover:bg-[#F5F7FA] hover:text-[#18365E]"
                    aria-label="Clear target date"
                    title="Clear date"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <label className="block space-y-1.5">
            <FieldLabel>Notes</FieldLabel>
            <textarea
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              placeholder="Optional notes"
              rows={3}
              className={`${FIELD_INPUT} resize-y min-h-[72px]`}
            />
          </label>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#FAFBFC] rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-3 py-2 text-xs font-semibold text-[#717182] hover:bg-[#F5F7FA] transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#18365E] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#142c4d] transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saving ? 'Saving…' : mode === 'add' ? 'Add workstream' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  row,
  onClose,
  onConfirm,
}: {
  row: WorkstreamRow;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, deleting]);

  const handleConfirm = async () => {
    setDeleting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete workstream.');
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => { if (!deleting) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-xl shadow-xl max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </span>
            <div className="space-y-1">
              <h3 id={titleId} className="text-sm font-semibold text-[#18365E]">
                Delete workstream?
              </h3>
              <p className="text-xs text-[#717182]">
                <span className="font-semibold text-[#18365E]">
                  {row.name || 'This workstream'}
                </span>{' '}
                will be removed from this initiative, along with its status and notes.
                This cannot be undone.
              </p>
            </div>
          </div>

          {error && (
            <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[#e5e7eb] bg-[#FAFBFC] rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-lg px-3 py-2 text-xs font-semibold text-[#717182] hover:bg-[#F5F7FA] transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-40 disabled:pointer-events-none"
          >
            {deleting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {deleting ? 'Deleting…' : 'Delete workstream'}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface WorkstreamsTableProps {
  workstreams: WorkstreamRow[];
  /** Create (no id) or update (id present). Rejecting keeps the modal open with the error. */
  onSave?: (row: WorkstreamRow) => Promise<void>;
  onDelete?: (row: WorkstreamRow) => Promise<void>;
}

export const WorkstreamsTable: React.FC<WorkstreamsTableProps> = ({
  workstreams,
  onSave,
  onDelete,
}) => {
  const editable = typeof onSave === 'function';
  // DMD-1210: at least one workstream must remain so defaults are not re-seeded.
  const canDelete = typeof onDelete === 'function' && workstreams.length > 1;
  const [modal, setModal] = useState<
    | { mode: 'add'; form: WorkstreamRow }
    | { mode: 'edit'; form: WorkstreamRow }
    | null
  >(null);
  const [pendingDelete, setPendingDelete] = useState<WorkstreamRow | null>(null);

  const openAdd = () => setModal({ mode: 'add', form: emptyForm() });

  const openEdit = (row: WorkstreamRow) => {
    setModal({
      mode: 'edit',
      form: {
        ...row,
        owner: normalizeOwnerForForm(row.owner),
        status: normalizeStatus(row.status),
        target: toDateInputValue(row.target),
      },
    });
  };

  const handleSave = async (row: WorkstreamRow) => {
    if (!onSave) return;
    await onSave(row);
    setModal(null);
  };

  // Errors surface inside the confirm dialog, which stays open so the user can retry.
  const handleConfirmedDelete = async (row: WorkstreamRow) => {
    if (!onDelete || !row.id) return;
    await onDelete(row);
    setPendingDelete(null);
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs" style={{ minWidth: 560 }}>
          <thead>
            <tr className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
              <th className="text-left px-1 py-1.5">Workstream</th>
              <th className="text-left px-1 py-1.5">Owner</th>
              <th className="text-left px-1 py-1.5">Status</th>
              <th className="text-left px-1 py-1.5">Target</th>
              <th className="text-left px-1 py-1.5 hidden md:table-cell">Notes</th>
              {editable && (
                <th className="w-16 px-1 py-1.5">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {workstreams.length === 0 ? (
              <tr>
                <td
                  colSpan={editable ? 6 : 5}
                  className="px-1 py-4 text-center text-xs text-[#717182]"
                >
                  No workstreams yet. Add one to get started.
                </td>
              </tr>
            ) : (
              workstreams.map((row, index) => {
                const status = normalizeStatus(row.status);
                return (
                  <tr key={row.id || `${row.name}-${index}`} className="border-t border-[#f1f5f9]">
                    <td className="px-1 py-2 font-semibold text-[#18365E]">{row.name || '—'}</td>
                    <td className="px-1 py-2 text-[#4b5563]">{row.owner || '—'}</td>
                    <td className="px-1 py-2">
                      <span
                        className={`inline-flex items-center gap-1.5 font-semibold ${STATUS_TEXT[status] ?? 'text-slate-500'
                          }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status] ?? 'bg-slate-300'}`}
                        />
                        {status}
                      </span>
                    </td>
                    <td className="px-1 py-2 text-[#4b5563]">{formatTargetForDisplay(row.target)}</td>
                    <td className="px-1 py-2 text-[#717182] hidden md:table-cell">
                      {row.notes || '—'}
                    </td>
                    {editable && (
                      <td className="px-1 py-2">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#94a3b8] transition-colors hover:bg-[#F5F7FA] hover:text-[#18365E]"
                            aria-label={`Edit ${row.name || 'workstream'}`}
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          {onDelete && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!canDelete) return;
                                setPendingDelete(row);
                              }}
                              disabled={!canDelete}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-transparent disabled:hover:text-[#94a3b8]"
                              aria-label={
                                canDelete
                                  ? `Delete ${row.name || 'workstream'}`
                                  : 'Cannot delete the last workstream'
                              }
                              title={
                                canDelete
                                  ? 'Delete'
                                  : 'At least one workstream is required'
                              }
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editable && (
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#cbd5e1] px-2.5 py-1.5 text-xs font-semibold text-[#18365E] transition-colors hover:border-[#18365E]/40 hover:bg-[#F5F7FA]"
        >
          <Plus className="h-3.5 w-3.5" />
          Add workstream
        </button>
      )}

      {modal && (
        <WorkstreamFormModal
          mode={modal.mode}
          initial={modal.form}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {pendingDelete && (
        <ConfirmDeleteModal
          row={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={() => handleConfirmedDelete(pendingDelete)}
        />
      )}
    </div>
  );
};

export default WorkstreamsTable;
